// Oldest browsers according to Ascension Google Analytics:
// IE >= 9, Safari >= 8, Chrome >= 28.x

// https://material-ui.com/guides/typescript/
import {
  createTheme,
  ThemeOptions,
  responsiveFontSizes,
} from '@mui/material/styles'
import { colors } from './colors'

// This theme isn't exported and is merely used as reference to get access to the default theme for a cleaner reference
const defaultTheme = createTheme()

// custom themes props:
// https://material-ui.com/guides/typescript/#customization-of-theme
// https://material-ui.com/customization/theming/#custom-variables
export const ascensionTheme: ThemeOptions = {
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
    values: {
      xs: 0,
      sm: 320,
      md: 688,
      lg: 992,
      xl: 1920,
    },
  },
  // direction: 'ltr',
  // mixins: {},
  components: {
    // Default props given to components
    MuiButton: {
      styleOverrides: {
        sizeLarge: {
          fontSize: '0.875rem',
        },
        textSizeLarge: {
          padding: '12px 11px',
        },
        outlinedSizeLarge: {
          padding: '11px 21px',
        },
        containedSizeLarge: {
          padding: '12px 22px',
        },
      },
      defaultProps: {
        size: 'large',
      },
    },
    // CssBaseline is same as Normalize
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          'html': {
            // fixes iOS text size issues.
            WebkitTextSizeAdjust: '100%',
          },
          // 'input[type="number"]': {
          //   '&::-webkit-inner-spin-button': {
          //     appearance: 'none',
          //   },
          // },
          // how to change base component styles via selectors:
          '.MuiButton-root': {
            // example: ...
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: '14.5px 14px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '4px',
        },
      },
    },
  },

  palette: {
    ...colors.brand,
    // white inverts primary color palette
    white: {
      main: defaultTheme.palette.primary.contrastText,
      contrastText: defaultTheme.palette.primary.main,
    },
  },
  typography: {
    fontFamily: [
      'HCo Whitney SSm',
      '-apple-system',
      'BlinkMacSystemFont',
      'Roboto',
      'San Francisco',
      'Helvetica Neue',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 600,
    subtitle1: { fontSize: '16px', lineHeight: 1.5, letterSpacing: '0.15px' },
    subtitle2: { fontSize: '14px', lineHeight: 1.71, letterSpacing: '0.1px' },
    body1: { fontSize: '16px', lineHeight: 1.5, letterSpacing: '0.5px' },
    body2: { fontSize: '14px', lineHeight: 1.43, letterSpacing: '0.25px' },
    button: {
      fontSize: '14px',
      lineHeight: 1.14,
      letterSpacing: '1.25px',
      textTransform: 'uppercase',
    },
    caption: { fontSize: '12px', lineHeight: 1.33, letterSpacing: '0.4px' },
    overline: {
      fontSize: '10px',
      lineHeight: 1.6,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
    },
  },
  shadows: [
    `none`,
    `0px 2px 1px -1px rgba(0,0,0,0.1),
     0px 1px 1px 0px rgba(0,0,0,0.07),
     0px 1px 3px 0px rgba(0,0,0,0.06)`,
    `0px 3px 1px -2px rgba(0,0,0,0.1),
     0px 2px 2px 0px rgba(0,0,0,0.07),
     0px 1px 5px 0px rgba(0,0,0,0.06)`,
    `0px 3px 3px -2px rgba(0,0,0,0.1),
     0px 3px 4px 0px rgba(0,0,0,0.07),
     0px 1px 8px 0px rgba(0,0,0,0.06)`,
    `0px 2px 4px -1px rgba(0,0,0,0.1),
     0px 4px 5px 0px rgba(0,0,0,0.07),
     0px 1px 10px 0px rgba(0,0,0,0.06)`,
    `0px 3px 5px -1px rgba(0,0,0,0.1),
     0px 5px 8px 0px rgba(0,0,0,0.07),
     0px 1px 14px 0px rgba(0,0,0,0.06)`,
    `0px 3px 5px -1px rgba(0,0,0,0.1),
     0px 6px 10px 0px rgba(0,0,0,0.07),
     0px 1px 18px 0px rgba(0,0,0,0.06)`,
    `0px 4px 5px -2px rgba(0,0,0,0.1),
     0px 7px 10px 1px rgba(0,0,0,0.07),
     0px 2px 16px 1px rgba(0,0,0,0.06)`,
    `0px 5px 5px -3px rgba(0,0,0,0.1),
     0px 8px 10px 1px rgba(0,0,0,0.07),
     0px 3px 14px 2px rgba(0,0,0,0.06)`,
    `0px 5px 6px -3px rgba(0,0,0,0.1),
     0px 9px 12px 1px rgba(0,0,0,0.07),
     0px 3px 16px 2px rgba(0,0,0,0.06)`,
    `0px 6px 6px -3px rgba(0,0,0,0.1),
     0px 10px 14px 1px rgba(0,0,0,0.07),
     0px 4px 18px 3px rgba(0,0,0,0.06)`,
    `0px 6px 7px -4px rgba(0,0,0,0.1),
     0px 11px 15px 1px rgba(0,0,0,0.07),
     0px 4px 20px 3px rgba(0,0,0,0.06)`,
    `0px 7px 8px -4px rgba(0,0,0,0.1),
     0px 12px 17px 2px rgba(0,0,0,0.07),
     0px 5px 22px 4px rgba(0,0,0,0.06)`,
    `0px 7px 8px -4px rgba(0,0,0,0.1),
     0px 13px 19px 2px rgba(0,0,0,0.07),
     0px 5px 24px 4px rgba(0,0,0,0.06)`,
    `0px 7px 9px -4px rgba(0,0,0,0.1),
     0px 14px 21px 2px rgba(0,0,0,0.07),
     0px 5px 26px 4px rgba(0,0,0,0.06)`,
    `0px 8px 9px -5px rgba(0,0,0,0.1),
     0px 15px 22px 2px rgba(0,0,0,0.07),
     0px 6px 28px 5px rgba(0,0,0,0.06)`,
    `0px 8px 10px -5px rgba(0,0,0,0.1),
     0px 16px 24px 2px rgba(0,0,0,0.07),
     0px 6px 30px 5px rgba(0,0,0,0.06)`,
    `0px 8px 11px -5px rgba(0,0,0,0.1),
     0px 17px 26px 2px rgba(0,0,0,0.07),
     0px 6px 32px 5px rgba(0,0,0,0.06)`,
    `0px 9px 11px -5px rgba(0,0,0,0.1),
     0px 18px 28px 2px rgba(0,0,0,0.07),
     0px 7px 34px 6px rgba(0,0,0,0.06)`,
    `0px 9px 12px -6px rgba(0,0,0,0.1),
     0px 19px 29px 2px rgba(0,0,0,0.07),
     0px 7px 36px 6px rgba(0,0,0,0.06)`,
    `0px 10px 13px -6px rgba(0,0,0,0.1),
     0px 20px 31px 3px rgba(0,0,0,0.07),
     0px 8px 38px 7px rgba(0,0,0,0.06)`,
    `0px 10px 13px -6px rgba(0,0,0,0.1),
     0px 21px 33px 3px rgba(0,0,0,0.07),
     0px 8px 40px 7px rgba(0,0,0,0.06)`,
    `0px 10px 14px -6px rgba(0,0,0,0.1),
     0px 22px 35px 3px rgba(0,0,0,0.07),
     0px 8px 42px 7px rgba(0,0,0,0.06)`,
    `0px 11px 14px -7px rgba(0,0,0,0.1),
     0px 23px 36px 3px rgba(0,0,0,0.07),
     0px 9px 44px 8px rgba(0,0,0,0.06)`,
    `0px 11px 15px -7px rgba(0,0,0,0.1),
     0px 24px 38px 3px rgba(0,0,0,0.07),
     0px 9px 46px 8px rgba(0,0,0,0.06)`,
  ],
  drawer: {
    breakpoint: 'lg',
    widthDown: 340,
    widthUp: 544,
  },
  scrollbar: {
    width: 11,
    borderWidth: 3,
  },
}

const theme = createTheme(ascensionTheme)
theme.typography.h1 = {
  ...theme.typography.h1,
  fontSize: '56px',
  lineHeight: 1.1667,
  letterSpacing: '-1.5px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '75px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '86px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '96px',
    lineHeight: 1.17,
  },
}
theme.typography.h2 = {
  ...theme.typography.h2,
  fontSize: '38px',
  lineHeight: 1.2,
  letterSpacing: '-0.5px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '47px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '53px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '60px',
  },
}
theme.typography.h3 = {
  ...theme.typography.h3,
  fontSize: '32px',
  lineHeight: 1.1667,
  [theme.breakpoints.up('sm')]: {
    fontSize: '41px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '45px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '48px',
  },
}
theme.typography.h4 = {
  ...theme.typography.h4,
  fontSize: '25px',
  lineHeight: 0.9444,
  [theme.breakpoints.up('sm')]: {
    fontSize: '29px',
    lineHeight: '27px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '32px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '34px',
    lineHeight: 1.06,
  },
}
theme.typography.h5 = {
  ...theme.typography.h5,
  fontSize: '20px',
  lineHeight: 1,
  [theme.breakpoints.up('sm')]: {
    fontSize: '21px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '24px',
  },
}
theme.typography.h6 = {
  ...theme.typography.h6,
  fontSize: '18px',
  lineHeight: 1.2,
  letterSpacing: '0.15px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '20px',
  },
}

const responsiveTheme = responsiveFontSizes(theme)

export { theme, responsiveTheme }
